// src/js/pages/Login.js
import { navigateTo } from "../framework/router.ts";
import { authContext } from "../context/AuthContext.ts";
import { login } from "../services/api.ts";
import Notification from "../components/Notification.ts";
import { LoginSkin } from "../components/skins/login.ts"; "../components/skins/login.js";

export default function LoginPage() {
  const container = document.createElement("div");
  container.classList.add("default_layout", "h-100");
  container.innerHTML = new LoginSkin().htmlskin();

  // Add tooltip element for hover MW reading, append to .mini-map-reading
  const miniMapReading = container.querySelector('.mini-map-reading');
  const tooltip = document.createElement("div");
  tooltip.id = "tooltip";
  miniMapReading.appendChild(tooltip); // Append to .mini-map-reading

  const loginForm = container.querySelector("#loginForm");
  const loginButton = loginForm.querySelector("button[type='submit']");

  loginForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    // 🔄 Loading state
    loginButton.disabled = true;
    const originalText = loginButton.textContent;
    loginButton.textContent = "Logging in…";
    try {
      // Call backend login
      const { user, token } = await login(formData.get("username"), formData.get("password"));
      // Update auth context with logged-in user
      authContext.login(user, token);
      Notification.show("Login successful!", "success");
      navigateTo("/overview");
      // if (user.licenseExpired) {
      //   navigateTo("/license-expired");
      //   return;
      // }
      // ✅ Redirect to overview
      //navigateTo("/overview");
    } catch (err) {
      console.error(err);
      Notification.show(err.message || "Login failed", "error");
    } finally {
      loginButton.disabled = false;
      loginButton.textContent = originalText;
    }
  });

  // Waveform Chart Integration
  const canvas = container.querySelector('#waveform-chart');
  const ctx = canvas.getContext('2d');

  // Set canvas size
  canvas.width = 800;
  canvas.height = 200;

  // Precisely approximated data points to match the image's waveform peaks (heights from bottom, scaled to 150 max)
  const data = [
    40, 45, 50, 55, 60, 65, 70, 65, 55, 45, // Initial small peak and dip
    35, 40, 45, 50, 55, 60, 70, 85, 95, 105, 115, 125, 115, 105, 95, // Rising to medium peak
    85, 90, 95, 100, 105, 110, 115, 120, 125, 130, 135, 140, 135, 130, // High peak
    125, 120, 115, 110, 105, 100, 95, 90, 85, 80, 75, // Dip
    70, 75, 80, 85, 90, 95, 100, 105, 110, 115, 120, 125, 130, 135, // Recovery
    140, 145, 150, 145, 140, 135, 130, 125, 120, 115, // High plateau
    110, 105, 100, 95, 90, 85, 80, 75, 70, // Gradual dip
    75, 80, 85, 90, 95, 100, 105, 110, 115, 120, // Rise
    125, 130, 135, 140, 145, 150, 150, 150, 150, 150 // Flat high end
  ];

  let time = 0; // For animation

  // Draw waveform
  function drawWaveform() {
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Create gradient fill
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, 'rgba(38, 166, 154, 0.6)'); // Slightly opaque teal at top
    gradient.addColorStop(0.7, 'rgba(38, 166, 153, 0.01)'); // Fade to lighter teal
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)'); // Transparent at bottom

    // Begin path for filled area
    ctx.beginPath();
    ctx.moveTo(0, canvas.height); // Start from bottom-left

    const numPoints = data.length;
    const step = canvas.width / (numPoints - 1);

    // Draw the waveform line with animation (subtle oscillation)
    ctx.lineTo(0, canvas.height - data[0] + Math.sin(time + 0) * 3);
    for (let i = 1; i < numPoints; i++) {
      const x = i * step;
      const y = canvas.height - data[i] + Math.sin(time + i * 0.05) * 3; // Subtle animation
      ctx.lineTo(x, y);
    }
    ctx.lineTo(canvas.width, canvas.height); // Close to bottom-right
    ctx.closePath();

    // Fill with gradient
    ctx.fillStyle = gradient;
    ctx.fill();

    // Draw outline
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#26A69A'; // Teal outline
    ctx.beginPath();
    ctx.moveTo(0, canvas.height - data[0] + Math.sin(time + 0) * 3);
    for (let i = 1; i < numPoints; i++) {
      const x = i * step;
      const y = canvas.height - data[i] + Math.sin(time + i * 0.05) * 3;
      ctx.lineTo(x, y);
    }
    ctx.stroke();

    // Add colorful noise at the bottom (mimicking image pixels)
    for (let x = 0; x < canvas.width; x++) {
      for (let y = canvas.height - 15; y < canvas.height; y++) {
        const prob = (canvas.height - y) / 15;
        if (Math.random() < prob) {
          ctx.fillStyle = `hsl(${Math.random() * 360}, 70%, 60%)`; // Varied saturation and lightness
          ctx.fillRect(x, y, 1, 1);
        }
      }
    }

    // Update time for animation
    time += 0.03; // Slower animation for subtlety
  }

  // Animation loop
  function animate() {
    drawWaveform();
    // requestAnimationFrame(animate);
  }

  // Start animation
  animate();

  // Hover event for MW reading
  canvas.addEventListener('mousemove', (e) => {
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const numPoints = data.length;
    const index = Math.round((mouseX / canvas.width) * (numPoints - 1));
    const mw = (data[index] / 10).toFixed(1); // Scale to MW (e.g., 150 -> 15.0 MW)

    tooltip.innerHTML = `${mw} MW`;
    // Position tooltip relative to canvas
    tooltip.style.left = `${mouseX + 10}px`; // Offset from mouse X within canvas
    tooltip.style.top = `${mouseY + 10}px`; // Offset from mouse Y within canvas
    tooltip.style.display = 'block';
  });

  canvas.addEventListener('mouseout', () => {
    tooltip.style.display = 'none';
  });

  // Optional: Redraw on window resize
  window.addEventListener('resize', () => {
    canvas.width = canvas.parentElement.offsetWidth;
    drawWaveform(); // Redraw once on resize (animation continues)
  });

  return container;
}